import { KaraokeTrackManager } from '../../lib/KaraokeTrackManager';
import { AbstractCommand } from '../AbstractCommand';

import type { DubRowRecord } from '../../types/karaoke';
import type { VideoEditor } from '../../VideoEditor';

/**
 * Edit a dubbed language's dub-timed caption rows (PLAT-11905): a line break, a
 * join, or a time offset.
 *
 * The edit is made on the row RECORD, never on the shown caption clips, because
 * the clips are derived again from the record on every re-fit — an edit written
 * on a clip would be gone after the next trim. So this applies one record edit,
 * derives the captions again, and its undo puts the previous record back and
 * derives again. (The per-command timeline snapshot restores elements only, and
 * the record is not an element.)
 *
 * It runs only while the dub-timed set is shown; the edit itself is a pure
 * function of the record, handed in by the editor's command methods.
 */
export class EditDubCaptionRowsCommand extends AbstractCommand {
  private readonly description: string;
  private readonly edit: (record: DubRowRecord) => DubRowRecord | null;
  private previous: DubRowRecord | null = null;
  private applied: DubRowRecord | null = null;

  constructor(
    editor: VideoEditor,
    description: string,
    edit: (record: DubRowRecord) => DubRowRecord | null,
  ) {
    super(editor);
    this.description = description;
    this.edit = edit;
  }

  getName(): string {
    return 'EditDubCaptionRowsCommand';
  }

  getDescription(): string {
    return this.description;
  }

  execute(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track || track.liveKind !== 'dub' || !track.dubRecord) return false;
    const current = track.dubRecord;
    // A redo re-applies the very record the first run made, so a split's new
    // row keeps its id.
    const next =
      this.applied && this.previous === current
        ? this.applied
        : this.edit(current);
    if (!next) return false;
    this.previous = current;
    this.applied = next;
    track.dubRecord = next;
    new KaraokeTrackManager(this.editor).reconcileDubCaptions();
    this.editor.markDirty('dub_caption_rows_edited');
    return true;
  }

  undo(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track || !this.previous) return false;
    track.dubRecord = this.previous;
    new KaraokeTrackManager(this.editor).reconcileDubCaptions();
    this.editor.markDirty('dub_caption_rows_edit_undone');
    return true;
  }
}
